import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import {
  selectImpersonatedUserId,
  selectIsImpersonating,
  useImpersonationSelector,
} from '@revelio/auth';
import type { FilterSet } from '@revelio/iso-utility';

import {
  createFilterSet,
  deleteFilterSet,
  FilterSetRequestError,
  getFilterSets,
  updateFilterSet,
} from '../../api/filter-sets-api';

export type SavedView = Pick<FilterSet, 'id' | 'name' | 'scope' | 'url'>;

type SavedViewResult = {
  id?: string;
  error?: { message: string; conflictingId?: string; isConflict: boolean };
};

type CreateSavedViewInput = Pick<FilterSet, 'name' | 'scope' | 'url'>;

type UpdateSavedViewInput = Pick<FilterSet, 'id'> &
  Partial<CreateSavedViewInput>;

export type UseSavedViews = {
  options: SavedView[];
  fetchingSavedViews: boolean;
  refresh: () => void;
  createView: (input: CreateSavedViewInput) => Promise<SavedViewResult>;
  updateView: (input: UpdateSavedViewInput) => Promise<SavedViewResult>;
  deleteView: (id: string) => Promise<SavedViewResult>;
  canManageViews: boolean;
};

const savedViewsQueryKey = (impersonatedUserId: string | null) =>
  ['saved-views', impersonatedUserId ?? 'self'] as const;

const SAVED_VIEW_MUTATION_META = {
  expectedErrorStatuses: [409],
  suppressErrorDialog: true,
};

const EMPTY_OPTIONS: SavedView[] = [];

const toResult = (error: unknown): SavedViewResult => {
  if (!error) return {};
  if (error instanceof FilterSetRequestError) {
    return {
      error: {
        message: error.message,
        conflictingId: error.conflictingId,
        isConflict: error.status === 409,
      },
    };
  }
  const message = error instanceof Error ? error.message : `${error}`;
  return { error: { message, isConflict: false } };
};

export const useSavedViews = (): UseSavedViews => {
  const impersonatedUserId = useImpersonationSelector(selectImpersonatedUserId);
  const isImpersonating = useImpersonationSelector(selectIsImpersonating);
  const queryClient = useQueryClient();
  const queryKey = useMemo(
    () => savedViewsQueryKey(impersonatedUserId),
    [impersonatedUserId]
  );

  const { data, isFetching } = useQuery({
    queryKey,
    queryFn: () => getFilterSets(impersonatedUserId ?? undefined),
    staleTime: 60_000,
    meta: { suppressErrorDialog: true },
  });

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey });
  }, [queryClient, queryKey]);

  const { mutateAsync: create } = useMutation({
    mutationKey: ['saved-views', 'create'],
    mutationFn: (input: CreateSavedViewInput) => createFilterSet(input),
    onSuccess: invalidate,
    meta: SAVED_VIEW_MUTATION_META,
  });

  const { mutateAsync: update } = useMutation({
    mutationKey: ['saved-views', 'update'],
    mutationFn: ({ id, ...input }: UpdateSavedViewInput) =>
      updateFilterSet(id, input),
    onSuccess: invalidate,
    meta: SAVED_VIEW_MUTATION_META,
  });

  const { mutateAsync: remove } = useMutation({
    mutationKey: ['saved-views', 'delete'],
    mutationFn: (id: string) => deleteFilterSet(id),
    onSuccess: invalidate,
    meta: SAVED_VIEW_MUTATION_META,
  });

  const settle = useCallback(
    async (
      request: () => Promise<{ id?: string } | undefined>
    ): Promise<SavedViewResult> => {
      try {
        const saved = await request();
        return saved?.id ? { id: saved.id } : {};
      } catch (error) {
        return toResult(error);
      }
    },
    []
  );

  return {
    options: data ?? EMPTY_OPTIONS,
    fetchingSavedViews: isFetching,
    refresh: invalidate,
    createView: (input) => settle(() => create(input)),
    updateView: (input) => settle(() => update(input)),
    deleteView: (id) => settle(() => remove(id)),
    canManageViews: !isImpersonating,
  };
};
